import React, { useState } from 'react';
import { Box, Flex, Text } from 'rebass/styled-components';
import { Button, FormInput, FormSelect, Message } from '@getro/rombo';
import { Field, Form, Formik } from 'formik';
import { Redirect } from 'react-router-dom';
import * as Yup from 'yup';

import strings from 'strings';
import { CompanyProfileLayout } from 'components/layout/CompanyProfileLayout';
import withMainLayout from 'hocs/withMainLayout';
import detectAtsFromUrl from 'helpers/ats/detectAtsFromUrl';
import useCompany from 'hooks/useCompany';
import usePageHook from 'hooks/usePageHook';
import usePermissions from 'hooks/usePermissions';
import { useDeleteCompanyAtsKeyMutation, useUpsertCompanyAtsKeyMutation } from 'services/companyAtsV2';
import { useAtsSettings } from 'hooks/useAtsSettings';
import { ContentSkeleton } from './skeletons/content';

const formSchema = Yup.object().shape({
  atsApiKey: Yup.string().required('API key is required'),
});

const CompanyAts = () => {
  const { company } = useCompany();
  usePageHook(strings.companyProfile.ats.pageTitle);
  const [justSaved, setJustSaved] = useState(false);
  const [upsertKey, { isLoading }] = useUpsertCompanyAtsKeyMutation();
  const [deleteKey, { isLoading: isDeleting }] = useDeleteCompanyAtsKeyMutation();
  const permissions = usePermissions({ companySlug: company?.slug || '' });

  const {
    shouldShowSkeleton,
    atsProvider,
    hasAtsKey,
    syncStatus,
    projectOptions,
    userOptions,
    initialProjectOption,
    initialUserOption,
    isGemSelectionLocked,
    savedProjectId,
  } = useAtsSettings(company, justSaved, setJustSaved);

  // Try to derive ATS from the company's first job source URL if available
  const jobSourceUrl = company?.organizationCareersPages?.[0]?.url || '';
  const detectedAts = detectAtsFromUrl(jobSourceUrl);

  const onSubmitConnectAts = async (values, { setSubmitting, setStatus, resetForm }) => {
    try {
      await upsertKey({
        companyId: company.id,
        apiKey: values.atsApiKey,
        provider: detectedAts?.key,
      }).unwrap();
      setStatus({ success: strings.companyProfile.ats.success });
      resetForm({ values: { atsApiKey: '' } });
    } catch (e) {
      setStatus({ error: strings.companyProfile.ats.error });
    } finally {
      setSubmitting(false);
    }
  };

  const bannerText =
    hasAtsKey && atsProvider ? `ATS connected to ${atsProvider}.` : strings.companyProfile.ats.status.titleNotConnected;

  // Redirect to 404 if user lacks permission to view this page
  if (company && !permissions.company.canManageGeneralInformation) {
    return <Redirect to="/404" />;
  }

  if (shouldShowSkeleton) {
    return (
      <CompanyProfileLayout description={strings.companyProfile.description}>
        <Box maxWidth="720px" role="status" aria-live="polite" aria-busy>
          <ContentSkeleton />
        </Box>
      </CompanyProfileLayout>
    );
  }

  return (
    <CompanyProfileLayout description={strings.companyProfile.description}>
      <Box maxWidth="720px">
        <Text fontSize="16px" color="text.dark" mb="16px" fontWeight="500">
          {strings.companyProfile.ats.descriptionTitle}
        </Text>
        <Box as="ul" mb="16px" pl="16px" lineHeight="body">
          {strings.companyProfile.ats.descriptionBullets.map((item) => (
            <li key={item}>
              <Text fontSize="14px" color="text.main">
                {item}
              </Text>
            </li>
          ))}
        </Box>

        {justSaved && (
          <Message type="success" mb="16px">
            {strings.companyProfile.ats.settings.savedSuccess}
          </Message>
        )}
        {!hasAtsKey && (
          <Message type="warning" mb="16px">
            {bannerText}
          </Message>
        )}

        {detectedAts?.name && (
          <Box mb="16px">
            <Text fontSize="14px" color="text.subtle">
              {strings.companyProfile.ats.detectedPrefix} {detectedAts.name}
            </Text>
            <Text fontSize="14px" color="text.subtle">
              {strings.companyProfile.ats.detectionNotePrefix}{' '}
              <a href={jobSourceUrl} target="_blank" rel="noopener noreferrer">
                {jobSourceUrl}
              </a>
            </Text>
          </Box>
        )}

        {!hasAtsKey && (
          <Formik
            enableReinitialize
            initialValues={{ atsApiKey: '' }}
            onSubmit={onSubmitConnectAts}
            validationSchema={formSchema}
          >
            {({ handleSubmit, status }) => (
              <Form onSubmit={handleSubmit}>
                {status?.error && <Message type="error">{status.error}</Message>}
                {status?.success && <Message type="success">{status.success}</Message>}
                <Field
                  name="atsApiKey"
                  label={strings.companyProfile.ats.apiKeyLabel}
                  labelProps={{ fontWeight: '600', fontSize: '16px', mb: '8px' }}
                  placeholder={strings.companyProfile.ats.apiKeyPlaceholder}
                  component={FormInput}
                  helpText={strings.companyProfile.ats.apiKeyHelp}
                  helpTextProps={{ fontSize: '14px' }}
                />
                <Flex mt="32px" gap="16px" justifyContent="flex-start">
                  <Button type="submit" loading={isLoading}>
                    {strings.companyProfile.ats.ctaConnect}
                  </Button>
                </Flex>
              </Form>
            )}
          </Formik>
        )}

        {hasAtsKey && syncStatus && (
          <Box mt="32px">
            <Text fontSize="12px" color="text.subtle" sx={{ fontVariantNumeric: 'tabular-nums' }}>
              {syncStatus.total} candidates synced
            </Text>
          </Box>
        )}

        {hasAtsKey && (
          <Formik
            enableReinitialize
            initialValues={{
              gemProjectId: initialProjectOption,
              gemUserId: initialUserOption,
            }}
            onSubmit={async (values, { setSubmitting, setStatus }) => {
              try {
                await upsertKey({
                  companyId: company.id,
                  provider: detectedAts?.key,
                  gemProjectId: values.gemProjectId?.value || null,
                  gemUserId: values.gemUserId?.value || null,
                }).unwrap();
                setJustSaved(true);
              } catch (e) {
                setStatus({ error: strings.companyProfile.ats.settings.savedError });
              } finally {
                setSubmitting(false);
              }
            }}
            validate={(values) => {
              if (isGemSelectionLocked) return {};
              const errors = {};
              if (!values.gemProjectId) errors.gemProjectId = 'Required';
              if (!values.gemUserId) errors.gemUserId = 'Required';
              return errors;
            }}
          >
            {({ handleSubmit, status, setFieldValue }) => (
              <Form onSubmit={handleSubmit}>
                {status?.error && <Message type="error">{status.error}</Message>}
                <Box mt="32px">
                  {isGemSelectionLocked ? (
                    <>
                      <Text as="h3" fontSize="14px" color="text.dark" mb="16px" fontWeight="500">
                        {strings.companyProfile.ats.settings.title}
                      </Text>
                      <Box mb="16px">
                        <Text fontSize="12px" color="text.subtle">
                          {strings.companyProfile.ats.settings.projectLabel}
                        </Text>
                        <Text fontSize="14px" color="text.main" sx={{ wordBreak: 'break-word' }}>
                          {initialProjectOption?.label || savedProjectId || strings.companyProfile.ats.settings.unknown}
                        </Text>
                      </Box>
                      <Box mb="16px">
                        <Text fontSize="12px" color="text.subtle">
                          {strings.companyProfile.ats.settings.ownerLabel}
                        </Text>
                        <Text fontSize="14px" color="text.main" sx={{ wordBreak: 'break-word' }}>
                          {initialUserOption?.label || strings.companyProfile.ats.settings.unknown}
                        </Text>
                      </Box>
                      <Text fontSize="12px" color="text.subtle" mt="32px">
                        {strings.companyProfile.ats.settings.lockedHint}
                      </Text>
                    </>
                  ) : (
                    <>
                      <Box mb="16px">
                        <Field
                          name="gemProjectId"
                          label="Gem project"
                          placeholder={strings.companyProfile.ats.settings.selectProject}
                          component={FormSelect}
                          options={projectOptions}
                          onChange={(opt) => setFieldValue('gemProjectId', opt || null)}
                        />
                      </Box>
                      <Field
                        name="gemUserId"
                        label="Gem owner"
                        placeholder={strings.companyProfile.ats.settings.selectOwner}
                        component={FormSelect}
                        options={userOptions}
                        onChange={(opt) => setFieldValue('gemUserId', opt || null)}
                      />
                    </>
                  )}
                </Box>
                {isGemSelectionLocked ? (
                  <Flex mt="32px" gap="16px" justifyContent="flex-start">
                    <Button
                      variant="danger"
                      type="button"
                      onClick={() => {
                        // eslint-disable-next-line no-alert
                        const ok = window.confirm(strings.companyProfile.ats.settings.disconnectConfirm);
                        if (ok) {
                          setJustSaved(false);
                          deleteKey({ companyId: company.id });
                        }
                      }}
                      loading={isDeleting}
                    >
                      {strings.companyProfile.ats.disconnect}
                    </Button>
                  </Flex>
                ) : (
                  <Flex mt="32px" sx={{ gap: '16px' }} justifyContent="flex-start">
                    <Button
                      variant="danger"
                      type="button"
                      onClick={() => {
                        // eslint-disable-next-line no-alert
                        const ok = window.confirm(strings.companyProfile.ats.settings.disconnectConfirm);
                        if (ok) {
                          setJustSaved(false);
                          deleteKey({ companyId: company.id });
                        }
                      }}
                      loading={isDeleting}
                    >
                      {strings.companyProfile.ats.disconnect}
                    </Button>
                    <Button type="submit">{strings.companyProfile.ats.settings.save}</Button>
                  </Flex>
                )}
              </Form>
            )}
          </Formik>
        )}
      </Box>
    </CompanyProfileLayout>
  );
};

export default withMainLayout(CompanyAts);
